// @flow
import React from 'react'
import I18n from 'i18n-react'
import Select from 'react-select/lib/Select'
import classNames from 'classnames'

import AbstractFormField from './AbstractFormField'
import { capitalizeFirstLetter } from '../../InputHelpFunctions'
import { sortObjectArrayByProp } from '../../SortFunctions'

// types
import type { ResourcesType } from '../../Types/Resources'
import type { DefaultFormFieldState, DefaultFormFieldType } from './AbstractFormField'

export type StringEnumRefIndexProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    values: Array<string>,
    resourceName: string,
    dependeeFieldName: string,
  },
  resources: ResourcesType,
  value?: {
    [key: string]: string,
  },
  sortByLabels?: boolean,
  matchPos?: string,
  preferredValues?: Array<string>,
}

type SelectedValue = {
  [key: string]: string,
}

export type StringEnumRefIndexState = DefaultFormFieldState & {
  options?: Array<SelectedValue>,
}

type SelectLabels = {
  [key: string]: string,
}

export type SelectSettings = {
  labels: SelectLabels,
  options: Array<Object>,
}

export default class StringEnumRefIndex extends AbstractFormField<StringEnumRefIndexProps, StringEnumRefIndexState> {
  static defaultProps = {
    matchPos: 'start',
  }

  static getDerivedStateFromProps(nextProps: StringEnumRefIndexProps, prevState: StringEnumRefIndexState) {
    return getDerivedStateFromPropsLogic(nextProps, prevState, getUpdatedSelectSettings)
  }

  constructor(props: StringEnumRefIndexProps) {
    super(props)
    this.state = {
      options: [],
      pristine: true,
      value: null,
      prevIsPristine: true,
      translationsLoaded: false,
    }
    this.selectRef = React.createRef()
  }

  handleOnchange = (value: SelectedValue) => {
    if (value) {
      this.setState({ value })
      this.props.onChange(value.value)
    } else {
      this.setState({ value: {} })
      this.props.onChange(null)
    }
  }

  handleOnBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.onBlur(event)
    if (event.target.value) {
      this.selectRef.current.selectFocusedOption()
    }
  }

  render() {
    if (this.props.textMode) {
      return this.getTextModeLabel()
    }

    return (
      <div className={classNames(this.getCssClasses())}>
        <Select
          autosize={false}
          clearAllText={I18n.translate('stringEnum.clearAllText')}
          clearValueText={I18n.translate('stringEnum.clearValueText')}
          disabled={!this.props.enabled}
          matchPos={this.props.matchPos}
          name={this.props.name}
          noResultsText={I18n.translate('stringEnum.noResultsText')}
          onBlur={this.handleOnBlur}
          onChange={this.handleOnchange}
          options={this.state.options}
          placeholder={I18n.translate(`select.${this.props.name}.placeholder`, {notFound: I18n.translate('stringEnum.placeholder')})}
          ref={this.selectRef}
          searchPromptText={I18n.translate('stringEnum.searchPromptText')}
          value={this.state.value}
          clearable={false}
        />
        {this.renderError()}
      </div>
    )
  }

  getTextModeLabel = () => {
    let label = this.props.value

    if (this.state.labels && this.state.labels[this.props.value]) {
      label = this.state.labels[this.props.value]
    }

    return <span>{label}</span>
  }

  getCssClasses = (): Array<string> => {
    const classes = ['react-select']

    if (this.hasErrors() && !this.isPristine()) {
      classes.push('is-invalid')
    }

    return classes
  }
}

export const getDerivedStateFromPropsLogic = (
  nextProps: StringEnumRefIndexProps,
  prevState: StringEnumRefIndexState,
  getSelectSettings: function,
) => {
  let changed = false
  const newState = {}

  if (
    nextProps.resources &&
    nextProps.resources[nextProps.domain.resourceName] &&
    nextProps.formData[nextProps.domain.dependeeFieldPath]
  ) {
    const { labels, options } = getSelectSettings(nextProps)

    changed = true
    newState.labels = labels
    newState.value = null
    newState.options = options

    if (nextProps.value) {
      newState.value = { value: nextProps.value, label: labels[nextProps.value] }
    }
  }

  if (nextProps.value !== prevState.prevPropsValue && !!nextProps.value && !!prevState.prevPropsValue) {
    changed = true
    newState.prevPropsValue = nextProps.value
  }

  if (
    !nextProps.isPristine !== !prevState.pristine &&
    nextProps.isPristine !== prevState.prevIsPristine &&
    !nextProps.isPristine
  ) {
    changed = true
    newState.pristine = false
    newState.prevIsPristine = nextProps.isPristine
  }

  if (I18n.texts && I18n.texts.translationLoaded !== prevState.translationsLoaded) {
    changed = true
    newState.translationsLoaded = I18n.texts.translationLoaded
  }

  return changed ? newState : null
}

export const getUpdatedSelectSettings = (nextProps: StringEnumRefIndexProps): SelectSettings => {
  const dependeeValue = nextProps.formData[nextProps.domain.dependeeFieldPath]

  return getSelectSettingsWithSortedLabels(getDefaultSelectSettings(nextProps, dependeeValue))
}

export const getDefaultSelectSettings = (nextProps: StringEnumRefIndexProps, dependeeValue: string): SelectSettings => {
  let settings = {
    labels: {},
    options: [],
  }

  if (dependeeValue && nextProps.resources[nextProps.domain.resourceName][dependeeValue]) {
    settings = nextProps.valueNames
      ? getLabelsAndOptionsForValueNames(nextProps, dependeeValue)
      : getDefaultLabelsAndOptions(nextProps, dependeeValue)
  }

  return settings
}

const getLabelsAndOptionsForValueNames = (nextProps: StringEnumRefIndexProps, dependeeValue: string): SelectLabels => {
  const options = []

  const labels = nextProps.resources[nextProps.domain.resourceName][dependeeValue].reduce((acc, value) => {
    let label = capitalizeFirstLetter(nextProps.valueNames[value] ? nextProps.valueNames[value] : value)

    acc[value] = label
    options.push({ value, label })

    return acc
  }, {})

  return {
    labels,
    options,
  }
}

const getDefaultLabelsAndOptions = (nextProps: StringEnumRefIndexProps, dependeeValue: string): SelectLabels => {
  const options = []

  const labels = nextProps.resources[nextProps.domain.resourceName][dependeeValue].reduce((acc, value) => {
    acc[value] = capitalizeFirstLetter(value)

    options.push({ value, label: acc[value] })

    return acc
  }, {})

  return {
    labels,
    options,
  }
}

export const getSelectSettingsWithSortedLabels = (settings: SelectSettings): SelectSettings => {
  return {
    ...settings,
    options: sortObjectArrayByProp(settings.options, 'label'),
  }
}
